import * as React from 'react';

import { observer } from 'mobx-react';
import styled from 'styled-components';

import { ContentSpacing } from '@estee/elc-base-theme';
import { IBaseIconSet } from '@estee/elc-icons';

import BaseLink from '~components/BaseLink';
import { classes } from '~utils/Classes';
import { sidebarSections as sdb } from '~views/sidebar/sections';

import { menuLinkMappings } from './menuLinkMappings';

export interface IMenuLink {
    label: string;
    menuKey: string;
    url: string;
    iconSetName: IBaseIconSet['iconSetName'];
}

interface IMenuProps {
    menu: IMenuLink[];
    onClick(link: IMenuLink): void;
}

interface IMenuLinkProps {
    link: IMenuLink;
    onClick: IMenuProps['onClick'];
}

interface ILinkIconAndLabel {
    link: IMenuLink;
}

const ICON_SIZE = 18;

const LinkUrl = styled(BaseLink)`
    align-items: center;
    display: flex;
    padding: ${ContentSpacing.space10} ${ContentSpacing.space20};

    svg {
        width: ${ICON_SIZE}px;
    }
    svg + span {
        margin: 0 ${ContentSpacing.space16};
    }
`;

const MenuLinks = styled.nav`
    align-content: flex-start;
    display: grid;
    gap: ${ContentSpacing.space4};
    width: 100%;
    padding: ${ContentSpacing.space8} 0 0;
    height: fit-content;
    grid-area: ${sdb.menu};
`;

const accountMenuLabelClass = classes('account-menu-label');
const LinkIconAndLabel = observer(function LinkIconAndLabel({
    link: { menuKey, label, iconSetName }
}: ILinkIconAndLabel) {
    const { icon: Icon = null, iconName = null, className = '' } = menuLinkMappings[menuKey] || {};

    return (
        <>
            {Icon && <Icon name={iconName} className={className} iconSetName={iconSetName} />}
            {label && <span className={accountMenuLabelClass}>{label}</span>}
        </>
    );
});

const MenuLink = observer(function MenuLink({ link, onClick }: IMenuLinkProps) {
    const onMenuClick = () => onClick(link);
    const accountMenuLinkClass = classes('account-menu-link', link.menuKey);

    return (
        <LinkUrl className={accountMenuLinkClass} href={link.url} onClick={onMenuClick}>
            <LinkIconAndLabel link={link} />
        </LinkUrl>
    );
});

export const TEST_ID_ACCOUNT_MENU = 'account-menu';

const accountMenuClass = classes('account-menu');

export const Menu = observer(function Menu({ menu = [], onClick }: IMenuProps) {
    return (
        <MenuLinks className={accountMenuClass} data-test-id={TEST_ID_ACCOUNT_MENU}>
            {menu.length > 0 &&
                menu.map((link: IMenuLink) => (
                    <MenuLink key={link.menuKey} link={link} onClick={onClick} />
                ))}
        </MenuLinks>
    );
});
